import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  InputAdornment,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Group as GroupIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { formatDateTime } from '../utils/dateTime';
import MobileRecordCard from '../components/common/MobileRecordCard';
import { Contact } from '../types';
import { contactsApi, bulkMessagingApi } from '../services/api';

interface Campaign {
  id: string;
  name: string;
  message_content: string;
  status: string;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  response_count: number;
  created_at: string;
  completed_at?: string;
  created_by?: string;
}

const BulkMessaging: React.FC = () => {
  // App chrome + table->card switches key off `down('md')` (see plan).
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const dateTimeFormat = useAuthStore((state) => state.user?.dateTimeFormat);

  // Fetch active contacts from database
  useEffect(() => {
    const fetchContacts = async () => {
      setIsLoading(true);
      try {
        const response = await contactsApi.getContacts({ status: 'active' });
        // API returns { success, data: [...], pagination }
        const rawData = response.data?.data || response.data || [];
        const dataArray = Array.isArray(rawData) ? rawData : [];
        
        // Filter to only non-archived contacts and map fields
        const contactData = dataArray
          .filter((c: any) => !c.is_archived && !c.isArchived)
          .map((c: any) => ({
            id: c.id,
            internalId: c.internal_id || c.internalId,
            firstName: c.first_name || c.firstName || '',
            lastName: c.last_name || c.lastName || '',
            fullName: c.full_name || c.fullName || `${c.first_name || c.firstName || ''} ${c.last_name || c.lastName || ''}`.trim(),
            phone: c.phone || c.mobile_phone || c.mobilePhone || '',
            email: c.email || '',
            status: c.status || 'active',
            isArchived: c.is_archived || c.isArchived || false,
            tags: c.tags || [],
            createdAt: c.created_at || c.createdAt || '',
            updatedAt: c.updated_at || c.updatedAt || '',
          }));
        setContacts(contactData);
      } catch (error) {
        console.error('Failed to fetch contacts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchContacts();
  }, []);

  // Fetch campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await bulkMessagingApi.getCampaigns(1, 50);
        setCampaigns(response.data?.data || []);
      } catch (error) {
        console.error('Failed to fetch campaigns:', error);
      }
    };
    
    fetchCampaigns();
  }, []);

  const filteredContacts = contacts.filter(contact => {
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      return (
        contact.fullName?.toLowerCase().includes(query) ||
        contact.phone?.includes(query) ||
        contact.internalId?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };

  const handleToggleContact = (contactId: string) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSendMessages = async () => {
    setIsSending(true);
    try {
      const response = await bulkMessagingApi.send(selectedContacts, message, campaignName);
      
      if (response.data?.success) {
        // Refresh campaigns list
        const campaignsResponse = await bulkMessagingApi.getCampaigns(1, 50);
        setCampaigns(campaignsResponse.data?.data || []);
        
        // Reset form
        setSelectedContacts([]);
        setMessage('');
        setCampaignName('');
        setConfirmDialogOpen(false);
        
        // Show success feedback (could add a snackbar here)
        const result = response.data.data;
        if (result.failedCount > 0) {
          alert(`Sent ${result.sentCount} of ${result.totalRecipients} messages. ${result.failedCount} failed.`);
        } else {
          alert(`Successfully sent ${result.sentCount} messages!`);
        }
      } else {
        alert('Failed to send messages: ' + (response.data?.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Failed to send messages:', error);
      alert('Failed to send messages: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSending(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'sending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Bulk Messaging
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Send messages to multiple contacts at once via Spruce
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Messages sent through bulk messaging will <strong>NOT</strong> create triage tasks. 
        However, if a contact responds, their response <strong>WILL</strong> create a new triage task.
      </Alert>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab icon={<SendIcon />} iconPosition="start" label="New Message" value="new" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Messaging History" value="history" />
        </Tabs>
      </Paper>

      {activeTab === 'new' && (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Contact Selection */}
          <Paper sx={{ flex: 1, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Select Recipients</Typography>
              <Chip
                icon={<GroupIcon />}
                label={`${selectedContacts.length} selected`}
                color={selectedContacts.length > 0 ? 'primary' : 'default'}
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                          indeterminate={selectedContacts.length > 0 && selectedContacts.length < filteredContacts.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContacts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            {searchTerm ? 'No contacts match your search' : 'No active contacts found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredContacts.map(contact => (
                        <TableRow
                          key={contact.id}
                          hover
                          onClick={() => handleToggleContact(contact.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedContacts.includes(contact.id)} />
                          </TableCell>
                          <TableCell>{contact.internalId || '-'}</TableCell>
                          <TableCell>{contact.fullName || `${contact.firstName} ${contact.lastName}`.trim() || '-'}</TableCell>
                          <TableCell>{contact.phone || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Message Composition */}
          <Paper sx={{ width: { xs: '100%', md: 400 }, p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Compose Message</Typography>

            <TextField
              fullWidth
              label="Campaign Name (optional)"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Message"
              multiline
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              sx={{ mb: 2 }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {message.length}/160 characters per SMS segment
            </Typography>

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={() => setConfirmDialogOpen(true)}
              disabled={selectedContacts.length === 0 || !message.trim()}
            >
              Send to {selectedContacts.length} Recipients
            </Button>
          </Paper>
        </Box>
      )}

      {activeTab === 'history' && (
        <Paper sx={{ p: 2 }}>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {campaigns.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No messaging history yet. Send your first bulk message to get started.
                </Typography>
              ) : (
                campaigns.map((campaign) => (
                  <MobileRecordCard
                    key={campaign.id}
                    title={campaign.name || 'Untitled'}
                    subtitle={`${(campaign.message_content || '').substring(0, 60)}${(campaign.message_content || '').length > 60 ? '...' : ''}`}
                    status={{ label: campaign.status?.replace(/_/g, ' ') || 'unknown', color: getStatusColor(campaign.status) as any }}
                    rows={[
                      { label: 'Date', value: campaign.created_at ? formatDateTime(campaign.created_at, dateTimeFormat) : '-' },
                      { label: 'Recipients', value: campaign.total_recipients || 0 },
                      { label: 'Sent', value: campaign.sent_count || 0 },
                      { label: 'Failed', value: campaign.failed_count || 0 },
                      { label: 'Responses', value: campaign.response_count || 0 },
                    ]}
                  />
                ))
              )}
            </Box>
          ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell>Failed</TableCell>
                  <TableCell>Responses</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No messaging history yet. Send your first bulk message to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  campaigns.map(campaign => (
                    <TableRow key={campaign.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{campaign.name || 'Untitled'}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {(campaign.message_content || '').substring(0, 50)}{(campaign.message_content || '').length > 50 ? '...' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{campaign.created_at ? formatDateTime(campaign.created_at, dateTimeFormat) : '-'}</TableCell>
                      <TableCell>{campaign.total_recipients || 0}</TableCell>
                      <TableCell>
                        <Chip icon={<SuccessIcon />} label={campaign.sent_count || 0} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {(campaign.failed_count || 0) > 0 ? (
                          <Chip icon={<ErrorIcon />} label={campaign.failed_count} size="small" color="error" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell>{campaign.response_count || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.status?.replace(/_/g, ' ') || 'unknown'}
                          size="small"
                          color={getStatusColor(campaign.status) as any}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Mass Message</DialogTitle>
        <DialogContent>
          {isSending ? (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ mb: 2 }}>Sending messages...</Typography>
              <LinearProgress />
            </Box>
          ) : (
            <Box>
              <Typography sx={{ mb: 2 }}>
                You are about to send the following message to <strong>{selectedContacts.length}</strong> recipients:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2">{message}</Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendMessages}
            disabled={isSending}
            startIcon={<SendIcon />}
          >
            Confirm & Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkMessaging;


